import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useToast } from '../lib/toast';
import VehicleForm from '../components/VehicleForm';
import MaintForm from '../components/MaintForm';
import BookingsView from './BookingsView';
import ReportsView from './ReportsView';
import {
  CATS, STATUSES, MAINT_STATUSES, PRIORITIES,
  priorityColor, priorityText, maintStatusColor, maintStatusText, vehicleStatusBadge,
} from '../lib/constants';

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { show } = useToast();

  const [view, setView] = useState('fleet');
  const [fleet, setFleet] = useState([]);
  const [maint, setMaint] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const [search, setSearch] = useState('');
  const [catF, setCatF] = useState('All');
  const [statusF, setStatusF] = useState('All');
  const [mSearch, setMSearch] = useState('');
  const [mStatus, setMStatus] = useState('All');
  const [mPriority, setMPriority] = useState('All');

  async function loadAll() {
    setLoading(true);
    try {
      const [v, m] = await Promise.all([api.get('/vehicles'), api.get('/maintenance')]);
      setFleet(v.data);
      setMaint(m.data);
    } catch (e) {
      show('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  async function saveVehicle(v) {
    try {
      const payload = { ...v };
      delete payload.id;
      delete payload.created_at;
      if (v.id) {
        const { data } = await api.put(`/vehicles/${v.id}`, payload);
        setFleet((p) => p.map((x) => (x.id === v.id ? data : x)));
        show('Vehicle updated', 'success');
      } else {
        const { data } = await api.post('/vehicles', payload);
        setFleet((p) => [data, ...p]);
        show('Vehicle added', 'success');
      }
      setModal(null);
    } catch (e) {
      show(formatApiErrorDetail(e.response?.data?.detail) || 'Save failed', 'error');
    }
  }

  async function deleteVehicle(id) {
    if (!window.confirm('Delete this vehicle? This cannot be undone.')) return;
    try {
      await api.delete(`/vehicles/${id}`);
      setFleet((p) => p.filter((x) => x.id !== id));
      setMaint((p) => p.filter((m) => m.vid !== id));
      show('Vehicle deleted', 'success');
    } catch (e) {
      show('Delete failed', 'error');
    }
  }

  async function saveMaint(m) {
    try {
      const payload = { ...m };
      delete payload.id;
      delete payload.created_at;
      if (m.id) {
        const { data } = await api.put(`/maintenance/${m.id}`, payload);
        setMaint((p) => p.map((x) => (x.id === m.id ? data : x)));
      } else {
        const { data } = await api.post('/maintenance', payload);
        setMaint((p) => [data, ...p]);
      }
      // Vehicle status may have changed — refresh fleet
      const v = await api.get('/vehicles');
      setFleet(v.data);
      show('Maintenance saved', 'success');
      setModal(null);
    } catch (e) {
      show(formatApiErrorDetail(e.response?.data?.detail) || 'Save failed', 'error');
    }
  }

  async function deleteMaint(id) {
    if (!window.confirm('Delete this maintenance record?')) return;
    try {
      await api.delete(`/maintenance/${id}`);
      setMaint((p) => p.filter((x) => x.id !== id));
      show('Record deleted', 'success');
    } catch (e) {
      show('Delete failed', 'error');
    }
  }

  async function quickComplete(record) {
    await saveMaint({
      ...record,
      status: 'Completed',
      done: new Date().toISOString().split('T')[0],
    });
  }

  const filtFleet = useMemo(() => {
    return fleet.filter((c) => {
      const q = search.toLowerCase();
      return (
        (!q || `${c.brand} ${c.model} ${c.cat} ${c.plate || ''}`.toLowerCase().includes(q)) &&
        (catF === 'All' || c.cat === catF) &&
        (statusF === 'All' || c.status === statusF)
      );
    });
  }, [fleet, search, catF, statusF]);

  const filtMaint = useMemo(() => {
    return maint.filter((m) => {
      const car = fleet.find((c) => c.id === m.vid);
      const q = mSearch.toLowerCase();
      return (
        (!q ||
          m.type.toLowerCase().includes(q) ||
          (car ? `${car.brand} ${car.model}`.toLowerCase().includes(q) : false) ||
          (m.tech || '').toLowerCase().includes(q)) &&
        (mStatus === 'All' || m.status === mStatus) &&
        (mPriority === 'All' || m.priority === mPriority)
      );
    });
  }, [maint, fleet, mSearch, mStatus, mPriority]);

  const critAlerts = maint.filter((m) => m.priority === 'Critical' && m.status !== 'Completed' && m.status !== 'Cancelled');
  const inMaintCount = fleet.filter((c) => c.status === 'In Maintenance').length;
  const openWO = maint.filter((m) => m.status === 'Scheduled' || m.status === 'In Progress').length;
  const openCost = maint
    .filter((m) => m.status !== 'Completed' && m.status !== 'Cancelled' && m.estCost)
    .reduce((s, m) => s + parseFloat(m.estCost || 0), 0);

  return (
    <div style={{ background: '#F1F5F9', minHeight: '100vh' }}>
      {modal === 'addVehicle' && <VehicleForm onSave={saveVehicle} onClose={() => setModal(null)} />}
      {modal?.type === 'editVehicle' && <VehicleForm initial={modal.data} onSave={saveVehicle} onClose={() => setModal(null)} />}
      {modal === 'addMaint' && <MaintForm fleet={fleet} onSave={saveMaint} onClose={() => setModal(null)} />}
      {modal?.type === 'editMaint' && <MaintForm initial={modal.data} fleet={fleet} onSave={saveMaint} onClose={() => setModal(null)} />}

      {/* Header */}
      <div style={{ background: '#0D1B3E', padding: '0 24px', position: 'sticky', top: 0, zIndex: 30, boxShadow: '0 2px 12px rgba(0,0,0,.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, maxWidth: 1240, margin: '0 auto', height: 64 }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ background: '#E63946', borderRadius: 8, padding: '6px 10px', fontSize: 18 }}>🚗</div>
            <span style={{ color: '#fff', fontWeight: 900, fontSize: 17, letterSpacing: '-.02em' }}>RENTCARSUS</span>
          </Link>
          <span style={{ color: 'rgba(255,255,255,.3)' }}>|</span>
          <span style={{ color: 'rgba(255,255,255,.65)', fontSize: 13 }}>Admin Backend</span>
          <div style={{ flex: 1 }} />
          {[
            ['fleet', '🚗 Fleet'],
            ['maintenance', '🔧 Maintenance'],
            ['bookings', '📅 Bookings'],
            ['reports', '📊 Reports'],
          ].map(([k, l]) => (
            <button
              key={k}
              data-testid={`nav-${k}`}
              onClick={() => setView(k)}
              style={{
                padding: '8px 18px',
                borderRadius: 8,
                border: 'none',
                background: view === k ? 'rgba(255,255,255,.18)' : 'transparent',
                color: view === k ? '#fff' : 'rgba(255,255,255,.65)',
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              {l}
            </button>
          ))}
          <span style={{ color: 'rgba(255,255,255,.55)', fontSize: 13, marginLeft: 8 }}>
            {user?.email || 'Admin'}
          </span>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              nav('/admin/login');
            }}
            style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid rgba(255,255,255,.25)', background: 'transparent', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            Logout
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 1240, margin: '0 auto', padding: 24 }}>
        {critAlerts.length > 0 && (
          <div data-testid="critical-alerts" style={{ background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 20 }}>🚨</span>
            <div>
              <strong style={{ color: '#991B1B', fontSize: 13 }}>
                {critAlerts.length} Critical Maintenance Alert{critAlerts.length > 1 ? 's' : ''}
              </strong>
              <p style={{ color: '#B91C1C', fontSize: 12, marginTop: 2 }}>
                {critAlerts
                  .map((a) => {
                    const c = fleet.find((x) => x.id === a.vid);
                    return `${c ? c.brand + ' ' + c.model : a.vid}: ${a.type}`;
                  })
                  .join(' · ')}
              </p>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div data-testid="kpi-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { l: 'Total Vehicles', v: loading ? '…' : fleet.length, ic: '🚗', col: '#0D1B3E', test: 'kpi-total' },
            { l: 'Available Now', v: loading ? '…' : fleet.filter((c) => c.status === 'Available').length, ic: '✅', col: '#10B981', test: 'kpi-available' },
            { l: 'In Maintenance', v: loading ? '…' : inMaintCount, ic: '🔧', col: '#F59E0B', test: 'kpi-maint' },
            { l: 'Open Work Orders', v: loading ? '…' : openWO, ic: '📋', col: '#E63946', test: 'kpi-wo' },
          ].map(({ l, v, ic, col, test }) => (
            <div key={l} data-testid={test} style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', padding: '18px 20px' }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{ic}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: col, lineHeight: 1 }}>{v}</div>
              <div style={{ fontSize: 12, color: '#64748B', fontWeight: 700, marginTop: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>{l}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8' }}>Loading…</div>
        ) : view === 'fleet' ? (
          <FleetView
            fleet={filtFleet}
            total={fleet.length}
            search={search} setSearch={setSearch}
            catF={catF} setCatF={setCatF}
            statusF={statusF} setStatusF={setStatusF}
            onAdd={() => setModal('addVehicle')}
            onEdit={(c) => setModal({ type: 'editVehicle', data: c })}
            onDelete={deleteVehicle}
          />
        ) : view === 'maintenance' ? (
          <MaintView
            records={filtMaint}
            fleet={fleet}
            mSearch={mSearch} setMSearch={setMSearch}
            mStatus={mStatus} setMStatus={setMStatus}
            mPriority={mPriority} setMPriority={setMPriority}
            openCost={openCost}
            onAdd={() => setModal('addMaint')}
            onEdit={(m) => setModal({ type: 'editMaint', data: m })}
            onDelete={deleteMaint}
            onQuickComplete={quickComplete}
          />
        ) : view === 'bookings' ? (
          <BookingsView />
        ) : (
          <ReportsView />
        )}
      </div>
    </div>
  );
}

function FleetView({ fleet, total, search, setSearch, catF, setCatF, statusF, setStatusF, onAdd, onEdit, onDelete }) {
  return (
    <div data-testid="fleet-view">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <input
          data-testid="fleet-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search vehicles..."
          style={{ flex: 1, minWidth: 200, padding: '10px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13 }}
        />
        <select data-testid="fleet-cat-filter" value={catF} onChange={(e) => setCatF(e.target.value)} style={{ padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
          <option>All</option>
          {CATS.map((c) => (<option key={c}>{c}</option>))}
        </select>
        <select data-testid="fleet-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)} style={{ padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
          <option>All</option>
          {STATUSES.map((s) => (<option key={s}>{s}</option>))}
        </select>
        <button data-testid="add-vehicle-btn" onClick={onAdd} className="btn-primary">＋ Add Vehicle</button>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Vehicle', 'Category', 'Price/Day', 'Status', 'Locations', 'Odometer', 'Actions'].map((h) => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fleet.map((c) => {
              const sc = vehicleStatusBadge[c.status] || vehicleStatusBadge.Available;
              return (
                <tr key={c.id} data-testid={`row-vehicle-${c.id}`} style={{ borderTop: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 10, background: `linear-gradient(135deg,${c.color}22,${c.color}44)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, overflow: 'hidden', flexShrink: 0 }}>
                        {c.img ? (
                          <img src={c.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }} />
                        ) : null}
                        <span style={{ display: c.img ? 'none' : 'block' }}>{c.emoji}</span>
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 14, color: '#0F172A' }}>{c.brand} {c.model}</div>
                        <div style={{ fontSize: 11, color: '#94A3B8' }}>{c.yr} · {c.plate || 'No plate'} · {c.trans}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: c.color + '22', color: c.color, borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700 }}>{c.cat}</span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ fontWeight: 900, fontSize: 17, color: '#0D1B3E' }}>${c.ppd}</span>
                    <span style={{ fontSize: 11, color: '#94A3B8' }}>/day</span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: sc.bg, color: sc.tx, borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700 }}>{c.status}</span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: '#64748B' }}>{(c.locs || []).length} locations</td>
                  <td style={{ padding: '14px 16px', fontSize: 13, color: '#0F172A' }}>{(c.odometer || 0).toLocaleString()} mi</td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button data-testid={`edit-vehicle-${c.id}`} onClick={() => onEdit(c)} style={{ padding: '7px 13px', borderRadius: 7, border: '1.5px solid #E2E8F0', background: '#fff', fontSize: 12, fontWeight: 700, color: '#0D1B3E', cursor: 'pointer' }}>✏️ Edit</button>
                      <button data-testid={`delete-vehicle-${c.id}`} onClick={() => onDelete(c.id)} style={{ padding: '7px 11px', borderRadius: 7, border: '1.5px solid #FECACA', background: '#FFF5F5', fontSize: 12, fontWeight: 700, color: '#991B1B', cursor: 'pointer' }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {fleet.length === 0 && (
              <tr><td colSpan={7} style={{ padding: '50px', textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>No vehicles match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 10 }}>{fleet.length} of {total} vehicles shown</p>
    </div>
  );
}

function MaintView({ records, fleet, mSearch, setMSearch, mStatus, setMStatus, mPriority, setMPriority, openCost, onAdd, onEdit, onDelete, onQuickComplete }) {
  return (
    <div data-testid="maint-view">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <input
          data-testid="maint-search"
          value={mSearch}
          onChange={(e) => setMSearch(e.target.value)}
          placeholder="Search by vehicle, type, technician..."
          style={{ flex: 1, minWidth: 220, padding: '10px 14px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13 }}
        />
        <select data-testid="maint-status-filter" value={mStatus} onChange={(e) => setMStatus(e.target.value)} style={{ padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
          <option>All</option>
          {MAINT_STATUSES.map((s) => (<option key={s}>{s}</option>))}
        </select>
        <select data-testid="maint-priority-filter" value={mPriority} onChange={(e) => setMPriority(e.target.value)} style={{ padding: '10px 12px', border: '1.5px solid #E2E8F0', borderRadius: 10, fontSize: 13, background: '#fff' }}>
          <option>All</option>
          {PRIORITIES.map((s) => (<option key={s}>{s}</option>))}
        </select>
        <button data-testid="add-maint-btn" onClick={onAdd} className="btn-red">＋ Log Maintenance</button>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #E2E8F0', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
          <thead>
            <tr style={{ background: '#F8FAFC' }}>
              {['Vehicle', 'Type', 'Priority', 'Status', 'Scheduled', 'Technician / Shop', 'Cost', 'Actions'].map((h) => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {records.map((m) => {
              const car = fleet.find((c) => c.id === m.vid);
              return (
                <tr key={m.id} data-testid={`row-maint-${m.id}`} style={{ borderTop: '1px solid #E2E8F0' }}>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0F172A' }}>{car ? `${car.brand} ${car.model}` : 'Unknown Vehicle'}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>{car?.plate || ''} {car?.yr ? `· ${car.yr}` : ''}</div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{m.type}</td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: priorityColor[m.priority], color: priorityText[m.priority], borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700 }}>{m.priority}</span>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <span style={{ background: maintStatusColor[m.status], color: maintStatusText[m.status], borderRadius: 20, padding: '3px 11px', fontSize: 11, fontWeight: 700 }}>{m.status}</span>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: '#64748B' }}>{m.sched || '—'}</td>
                  <td style={{ padding: '14px 16px', fontSize: 12, color: '#0F172A' }}>{m.tech || '—'}</td>
                  <td style={{ padding: '14px 16px', fontSize: 13 }}>
                    {m.actCost ? (
                      <>
                        <strong style={{ color: '#0D1B3E' }}>${parseFloat(m.actCost).toFixed(2)}</strong>
                        <span style={{ color: '#94A3B8', fontSize: 11 }}> actual</span>
                      </>
                    ) : m.estCost ? (
                      <span style={{ color: '#64748B' }}>${parseFloat(m.estCost).toFixed(2)} est.</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {m.status !== 'Completed' && m.status !== 'Cancelled' && (
                        <button data-testid={`done-maint-${m.id}`} onClick={() => onQuickComplete(m)} style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #BBF7D0', background: '#F0FDF4', fontSize: 11, fontWeight: 700, color: '#166534', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          ✓ Done
                        </button>
                      )}
                      <button data-testid={`edit-maint-${m.id}`} onClick={() => onEdit(m)} style={{ padding: '6px 10px', borderRadius: 7, border: '1.5px solid #E2E8F0', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>✏️</button>
                      <button data-testid={`delete-maint-${m.id}`} onClick={() => onDelete(m.id)} style={{ padding: '6px 8px', borderRadius: 7, border: '1.5px solid #FECACA', background: '#FFF5F5', fontSize: 11, fontWeight: 700, color: '#991B1B', cursor: 'pointer' }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {records.length === 0 && (
              <tr><td colSpan={8} style={{ padding: '50px', textAlign: 'center', color: '#94A3B8', fontSize: 14 }}>No records match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p style={{ fontSize: 12, color: '#94A3B8', marginTop: 10 }}>
        {records.length} records · Open est. cost: <strong>${openCost.toFixed(2)}</strong>
      </p>
    </div>
  );
}
